import React, { useEffect, useState } from "react";
import Box from "@amzn/meridian/box";
import Button from "@amzn/meridian/button";
import Text from "@amzn/meridian/text";
import Pagination from "@amzn/meridian/pagination";
import Textarea from "@amzn/meridian/textarea";
import Row from "@amzn/meridian/row";
import Link from "@amzn/meridian/link";
import Divider from "@amzn/meridian/divider";
import Thumbnail from "@amzn/meridian/thumbnail";
import Icon from "@amzn/meridian/icon";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import { useAppSelector } from "../../app/store/hooks";
import icon from "./icon.png";
import { GetArtifactsRequest, useGetCommentsMutation } from "../../endpoints/getArtifacts";
import LoaderBox from "../loader/LoaderBox";
import { formatDate } from "../../utils/formattingUtils";
import { useCreateArtifactMutation } from "../../endpoints/createArtifact";
import { useDeleteArtifactByIdMutation } from "../../endpoints/deleteArtifactById";
import "./style.module.scss";
import Column from "@amzn/meridian/column";
import { TextType } from "@amzn/meridian/text/text";
import { ThumbnailSize } from "@amzn/meridian/thumbnail/thumbnail";
import { BoxType } from "@amzn/meridian/box/box";
import { DEFAULT_LIMIT_PER_REQUEST } from "../../constants/defaultsConstants";
import refreshTokens from "@amzn/meridian-tokens/base/icon/refresh";

export interface ISectionProps {
    thumbnailSize: ThumbnailSize;
    commentedByTextType: TextType;
    createdOnTextType: TextType;
    type?: BoxType;
}

export interface ICommentProps {
    artifactId: string;
    artifactType: string;
    sectionProps: ISectionProps;
}

export const CommentsSection: React.FC<ICommentProps> = props => {
    const { firstName, alias } = useAppSelector(state => state.auth.user);
    const [commentText, setCommentText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [isAddCommentButtonDisabled, setIsAddCommentButtonDisabled] = useState(true);
    const [hasVisitedLastPage, setHasVisitedLastPage] = useState(false);
    const [comments, setComments] = useState([]);
    const [hasMoreComments, setHasMoreComments] = useState(true);

    const COMMENTS_PER_PAGE = 2;

    const [request, setRequest] = useState({
        useCase: "GET_ASSOCIATED_COMMENTS",
        businessUseCase: "SMART",
        queryParams: {
            requestNumber: 1,
            limitPerRequest: DEFAULT_LIMIT_PER_REQUEST,
            associatedArtifactId: props.artifactId,
            associatedArtifactType: props.artifactType,
        },
    });

    const [isLoading, setIsLoading] = useState(true);
    const [isSuccess, setIsSuccess] = useState(true);
    const [getComments] = useGetCommentsMutation();
    const [createArtifact] = useCreateArtifactMutation();
    const [deleteArtifact] = useDeleteArtifactByIdMutation();

    const totalPages = Math.ceil(comments.length / COMMENTS_PER_PAGE);
    const paginatedComments = getPaginatedComments(comments, currentPage, COMMENTS_PER_PAGE);

    useEffect(() => {
        getCommentsList();
    }, []);

    useEffect(() => {
        if (currentPage === totalPages && !hasVisitedLastPage && hasMoreComments) {
            setHasVisitedLastPage(true);
            const requestNumber = Math.ceil(comments.length / DEFAULT_LIMIT_PER_REQUEST) + 1;
            setRequest({
                useCase: "GET_ASSOCIATED_COMMENTS",
                businessUseCase: "SMART",
                queryParams: {
                    requestNumber,
                    limitPerRequest: DEFAULT_LIMIT_PER_REQUEST,
                    associatedArtifactId: props.artifactId,
                    associatedArtifactType: props.artifactType,
                },
            });
        }
    }, [currentPage, totalPages, hasVisitedLastPage, hasMoreComments]);

    useEffect(() => {
        setIsAddCommentButtonDisabled(commentText === "");
    }, [commentText]);

    const handleGetArtifact = async (request: GetArtifactsRequest) => {
        try {
            return await getComments(request);
        } catch (error) {
            // TODO: Display error message in UI.
            console.error("Error happened while fetching comments: " + error);
        }
    };

    const handleCreateArtifact = async (
        commentedBy: string,
        text: string,
        associatedArtifactType: string,
        associatedArtifactId: string
    ) => {
        try {
            return await createArtifact({
                artifactType: "COMMENT",
                businessUseCase: "SMART",
                clientName: alias,
                artifactDetails: {
                    commentedBy,
                    text,
                    associatedArtifactType,
                    associatedArtifactId,
                },
            });
        } catch (error) {
            // TODO: Display error message in UI.
            console.error("Error happened while creating comments: " + error);
        }
    };

    const handleDeleteArtifact = async (artifactId: string, artifactType: string) => {
        try {
            return await deleteArtifact({
                artifactType,
                artifactId,
                businessUseCase: "SMART",
                clientName: alias,
            });
        } catch (error) {
            // TODO: Display error message in UI.
            console.error("Error happened while deleting comments: " + error);
        }
    };

    const getCommentsList = async () => {
        try {
            setIsLoading(true);
            setIsSuccess(false);
            const response = handleGetArtifact(request);
            response.then(value => {
                const updatedComments = [...value.data.data.dataList];
                const hasMoreComments = updatedComments.length < value.data.data.totalCount;

                setComments([...updatedComments]);
                setHasMoreComments(hasMoreComments);
                setIsLoading(false);
                setIsSuccess(true);
            });
        } catch (error) {
            setIsLoading(false);
            setIsSuccess(true);
            // TODO: Display error message in UI.
            console.error("Error happened while fetching comments: " + error);
        }
    };

    const addComment = async () => {
        setIsLoading(true);
        setIsSuccess(false);

        await handleCreateArtifact(firstName, commentText, props.artifactType, props.artifactId);

        setCommentText("");
        setIsAddCommentButtonDisabled(true);
        setCurrentPage(1);
        setHasVisitedLastPage(false);

        getCommentsList();
    };

    const deleteComment = async (commentId: string) => {
        setComments(prevComments => {
            const updatedComments = prevComments.filter(
                comment => comment.artifactId !== commentId
            );
            const updatedCommentsLength = updatedComments.length;

            const prevTotalPages = Math.ceil(prevComments.length / COMMENTS_PER_PAGE);
            const updatedTotalPages =
                updatedCommentsLength !== prevComments.length
                    ? Math.ceil(updatedCommentsLength / COMMENTS_PER_PAGE)
                    : prevTotalPages;

            const shouldUpdateCurrentPage =
                paginatedComments.length === 1 &&
                ((currentPage !== 1 && currentPage > updatedTotalPages) ||
                    currentPage === totalPages);

            if (shouldUpdateCurrentPage) {
                setCurrentPage(updatedTotalPages);
            }

            if (updatedTotalPages !== totalPages) {
                setHasVisitedLastPage(false);
            }

            return updatedComments;
        });

        await handleDeleteArtifact(commentId, "COMMENT");
    };

    const handlePageChange = (newPage: number) => {
        if (newPage !== totalPages || newPage > currentPage) {
            setHasVisitedLastPage(false);
        }

        setCurrentPage(newPage);
    };

    return (
        <>
            {isLoading === false && isSuccess === true ? (
                <>
                    <Box type={props.sectionProps.type} spacingInset="250 450 450 450">
                        <Row width={"100%"} widths={["80%", "20%"]}>
                            <Column>
                                <Text type="h200">Comments</Text>
                            </Column>
                            <Column alignmentHorizontal={"end"}>
                                <Button type={"icon"} onClick={getCommentsList}>
                                    <Icon tokens={refreshTokens} color="primary" />
                                </Button>
                            </Column>
                        </Row>

                        <Box height="10px" />

                        <Row
                            width="fill"
                            widths={["fill", "fit"]}
                            wrap={"down"}
                            alignmentVertical="top"
                            alignmentHorizontal="end"
                        >
                            <Textarea value={commentText} onChange={setCommentText} error={false} />
                            <Button disabled={isAddCommentButtonDisabled} onClick={addComment}>
                                Add
                            </Button>
                        </Row>

                        <Box height="25px" />
                        <Divider size="medium" />
                        <Box height="20px" />

                        {paginatedComments.map((comment, index) => (
                            <React.Fragment key={index}>
                                <Row width="fit" widths={["fit", "fill", "fit"]}>
                                    <Column>
                                        <Thumbnail
                                            source={icon}
                                            size={props.sectionProps.thumbnailSize}
                                            type="circular"
                                        />
                                    </Column>
                                    <Column>
                                        <Box>
                                            <Text type={props.sectionProps.commentedByTextType}>
                                                {comment.artifactDetailsMap.commentedBy}
                                            </Text>
                                            <Text type={props.sectionProps.createdOnTextType}>
                                                {formatDate(comment.createdOn)}
                                            </Text>
                                        </Box>
                                    </Column>
                                    <Column>
                                        {firstName === comment.artifactDetailsMap.commentedBy && (
                                            <Link href={comment.artifactId} onClick={deleteComment}>
                                                <Icon
                                                    className={"delete-icon"}
                                                    tokens={trashTokens}
                                                ></Icon>
                                            </Link>
                                        )}
                                    </Column>
                                </Row>
                                <Box height="10px" />
                                <Row width="fit" widths={["93%", "7%"]}>
                                    <Text>{comment.artifactDetailsMap.text}</Text>
                                </Row>
                                <Box height="20px" />
                            </React.Fragment>
                        ))}
                        <Row alignmentHorizontal="end">
                            <Pagination
                                onChange={handlePageChange}
                                currentPage={currentPage}
                                showSkipArrows={false}
                                disabled={false}
                                numberOfPages={totalPages}
                            />
                        </Row>
                    </Box>
                </>
            ) : (
                <>
                    <LoaderBox loadingText={"Loading comments..."} />
                </>
            )}
        </>
    );
};

const getPaginatedComments = (comments: any, currentPage: number, commentsPerPage: number) => {
    const indexOfLastComment = currentPage * commentsPerPage;
    const indexOfFirstComment = indexOfLastComment - commentsPerPage;

    return comments.slice(indexOfFirstComment, indexOfLastComment);
};
